import {
  PhoneIncoming,
  Headphones,
  PhoneOutgoing,
  ClipboardList,
  Clock,
  UserCheck,
  PhoneForwarded,
  CalendarCheck,
  BookOpen,
  MessagesSquare,
  ArrowUpRight,
  FileText,
  Gauge,
  Radio,
  Mic,
  Shuffle,
  ListChecks,
  BarChart3,
  ConciergeBell,
  Languages,
  BedDouble,
} from 'lucide-react';
import { PhoneGraphic, ChatGraphic, HeadsetGraphic, ChartGraphic } from './solutionGraphics';

/* =========================================================
   Solutions.

   One entry per solution. The index at /site/solutions and every detail page
   at /site/solutions/:slug are generated from this array — adding a solution
   means adding an object here and nothing else.

   `wash` tints the hero panel on a detail page; `cardWash`/`tint` match the
   industry cards so both grids read as one set.

   Capability copy describes what the platform actually does today. Where a
   claim maps to a specific piece of the system it is noted in a comment, so
   the marketing copy and the product stay honest with each other.
   ========================================================= */

export const SOLUTIONS = [
  {
    slug: 'ai-receptionist',
    /* Played inline instead of a product video. File is not in the repo —
       see public/audio/README.md. Absent file = no player rendered. */
    sample: {
      src: '/audio/ai-receptionist.mp3',
      label: 'An after-hours call, answered',
      caption: 'Caller reaches a clinic at 9pm and books a slot',
    },
    name: 'AI Receptionist',
    tagline: 'Answer every call. Route every caller.',
    summary:
      'Picks up on the first ring, works out what the caller needs, and either handles it outright or puts them through to the right person.',
    graphic: PhoneGraphic,
    wash: 'bg-teal-50',
    cardWash: 'from-teal-100/70 to-teal-50/30',
    tint: 'bg-teal-50 text-teal-600',
    icon: PhoneIncoming,
    /* Every capability below exists: the handoff and calendar nodes are real
       workflow node types, and the greeting comes from the script's greeting
       node. */
    capabilities: [
      { name: 'Answers around the clock', icon: Clock, desc: 'No queue, no voicemail, no after-hours gap. Every caller reaches someone.' },
      { name: 'Qualifies before it routes', icon: UserCheck, desc: 'Captures who is calling and what they need, so whoever picks up already has the context.' },
      { name: 'Hands off to a person', icon: PhoneForwarded, desc: 'Dials a real extension when the call needs one, and takes the call back if nobody answers.' },
      { name: 'Books into your calendar', icon: CalendarCheck, desc: 'Offers times against real availability and writes the meeting back when the caller agrees.' },
    ],
    steps: [
      'A call comes in on your number',
      'The agent greets, and finds out what they need',
      'It answers, books, or dials a person',
      'You get the transcript and the outcome',
    ],
    industries: ['Healthcare', 'Hotels', 'Main Street Businesses', 'Financial Services'],
  },

  {
    slug: 'customer-service',
    /* Played inline instead of a product video. File is not in the repo —
       see public/audio/README.md. Absent file = no player rendered. */
    sample: {
      src: '/audio/customer-service.mp3',
      label: 'A billing question, resolved',
      caption: 'Answered from the uploaded policy doc, no handoff',
    },
    name: 'Customer Service',
    tagline: 'Resolve the question, not just the conversation.',
    summary:
      'Answers from your own documentation across voice, SMS, chat and email — and escalates cleanly when a question needs a person.',
    graphic: ChatGraphic,
    wash: 'bg-emerald-50',
    cardWash: 'from-emerald-100/70 to-emerald-50/30',
    tint: 'bg-emerald-50 text-emerald-600',
    icon: Headphones,
    /* Grounded in the knowledge-base + RAG stack: uploaded documents are
       chunked and embedded, and the agent retrieves per turn. */
    capabilities: [
      { name: 'Answers from your documents', icon: BookOpen, desc: 'Upload your policies and product docs; the agent retrieves the relevant passage on every turn rather than guessing.' },
      { name: 'One agent, four channels', icon: MessagesSquare, desc: 'The same script and the same knowledge answer on voice, SMS, chat and email.' },
      { name: 'Escalates on purpose', icon: ArrowUpRight, desc: 'Hands the conversation to a person when it should, with everything said so far attached.' },
      { name: 'Every conversation analysed', icon: FileText, desc: 'Outcome, sentiment and a summary written to the log automatically, with contact details extracted.' },
    ],
    steps: [
      'Point the agent at your knowledge base',
      'It answers on whichever channel the customer used',
      'Anything it should not answer goes to a person',
      'Outcome and summary land in the log',
    ],
    industries: ['Retail & eCommerce', 'Financial Services', 'Education', 'Healthcare'],
  },

  {
    slug: 'call-center',
    /* Played inline instead of a product video. File is not in the repo —
       see public/audio/README.md. Absent file = no player rendered. */
    sample: {
      src: '/audio/call-center.mp3',
      label: 'An outbound reminder call',
      caption: 'Appointment confirmed and written back to the record',
    },
    name: 'Call Center',
    tagline: 'Inbound and outbound, at real volume.',
    summary:
      'Runs campaigns and answers queues at genuine concurrency, with the compute scaling itself up when the calls start and back down when they stop.',
    graphic: HeadsetGraphic,
    wash: 'bg-violet-50',
    cardWash: 'from-violet-100/70 to-violet-50/30',
    tint: 'bg-violet-50 text-violet-600',
    icon: PhoneOutgoing,
    /* Capacity figure is the real one: services/gpuScaler.js runs 5 GPUs x 5
       workers = 25 concurrent voice channels, and autoscales on call volume. */
    capabilities: [
      { name: '25 concurrent calls', icon: Gauge, desc: 'Voice compute scales up on the first call of a campaign and back down when the queue clears.' },
      { name: 'Campaign dialling', icon: Radio, desc: 'Load a contact list and run it down, with per-number pacing and live progress.' },
      { name: 'Knows a machine from a person', icon: Mic, desc: 'Answering-machine detection ends voicemail calls instead of surveying an answerphone.' },
      { name: 'Recorded and transcribed', icon: FileText, desc: 'Every call is recorded and transcribed against the contact, ready for QA.' },
    ],
    steps: [
      'Load the list and pick the script',
      'Compute warms up before the first dial',
      'Calls run, machines are dropped, people are handled',
      'Recordings and outcomes land per contact',
    ],
    industries: ['Healthcare', 'Blood Banks', 'Financial Services', 'Retail & eCommerce'],
  },

  {
    slug: 'research-and-polling',
    /* Played inline instead of a product video. File is not in the repo —
       see public/audio/README.md. Absent file = no player rendered. */
    sample: {
      src: '/audio/research-and-polling.mp3',
      label: 'A survey interview',
      caption: 'Randomised block, strict answer matching in action',
    },
    name: 'Research & Polling',
    tagline: 'CATI surveys that actually finish.',
    summary:
      'Runs structured interviews at scale — randomised question groups, strict answer matching, and quotas that close themselves when a cell fills.',
    graphic: ChartGraphic,
    wash: 'bg-sky-50',
    cardWash: 'from-sky-100/70 to-sky-50/30',
    tint: 'bg-sky-50 text-sky-600',
    icon: ClipboardList,
    /* Every item maps to something real: the sequence node with `randomize`,
       the per-question `strictAnswer` toggle, backend/quotas.js, and the
       opinion analysis + reports stack. */
    capabilities: [
      { name: 'Randomised question groups', icon: Shuffle, desc: 'Rotate question order within a block to keep order effects out of the data.' },
      { name: 'Answers matched, not guessed', icon: ListChecks, desc: 'Strict mode accepts only a listed option, clarifies an ambiguous one, and never force-fits a response.' },
      { name: 'Quotas that close themselves', icon: Gauge, desc: 'Track cells as they fill and stop calling into one that is already complete.' },
      { name: 'Analysis and reports built in', icon: BarChart3, desc: 'Responses are coded, aggregated and reportable without exporting to something else.' },
    ],
    steps: [
      'Build the instrument as a question group',
      'Set quotas per cell',
      'The agent interviews and records each answer',
      'Results aggregate into a report as fielding runs',
    ],
    industries: ['Research (CATI)', 'Healthcare', 'Education', 'Blood Banks'],
  },

  {
    slug: 'hotel-front-desk',
    /* Played inline instead of a product video. File is not in the repo —
       see public/audio/README.md. Absent file = no player rendered. */
    sample: {
      src: '/audio/hotel-front-desk.mp3',
      label: 'A guest reservation, taken',
      caption: 'Room found, booked, and confirmed by text',
    },
    name: 'Hotel Front Desk',
    tagline: 'Your front desk never misses a guest.',
    summary:
      'Answers every call and message, completes booking and service workflows, and brings in your team when the moment needs a person.',
    graphic: PhoneGraphic,
    wash: 'bg-cyan-50',
    cardWash: 'from-cyan-100/70 to-cyan-50/30',
    tint: 'bg-cyan-50 text-cyan-600',
    icon: ConciergeBell,
    /* Real capabilities: the workflow node engine runs the concierge flow, the
       booking sub-flow captures guests/dates, OpenAI transcription handles the
       bilingual turn, and the human-handoff node dials a person. */
    capabilities: [
      { name: "Answers in the guest's language", icon: Languages, desc: 'Handles English and Mandarin on the same call, replying in whichever language the guest speaks.' },
      { name: 'Takes the reservation', icon: BedDouble, desc: 'Checks dates and party size, confirms availability, and captures the booking with a confirmation sent back.' },
      { name: 'Routes every request', icon: MessagesSquare, desc: 'Housekeeping, maintenance, room service and wake-up calls reach the right team, logged against the room.' },
      { name: 'Hands off to your team', icon: PhoneForwarded, desc: 'Escalates VIPs, complaints and anything sensitive to a person, with the conversation attached.' },
    ],
    steps: [
      'A guest calls your front desk line',
      'The agent greets and finds out what they need',
      'It answers, books, or routes the request',
      'Your team gets the transcript and the outcome',
    ],
    industries: ['Hotels', 'Resorts', 'Vacation Rentals', 'Hospitality Groups'],
  },
];

export const findSolution = (slug) => SOLUTIONS.find((s) => s.slug === slug) || null;
